import BigNumber from 'bignumber.js'
import fromPairs from 'lodash/fromPairs'
import { BigNumber as EthersBigNumber } from '@ethersproject/bignumber'
import poolsConfig from 'config/constants/pools'
import sousChefABI from 'config/abi/sousChef.json'
import erc20ABI from 'config/abi/erc20.json'
import multicall, { multicallv2 } from 'utils/multicall'
import { getAddress } from 'utils/addressHelpers'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { findToken } from '@pancakeswap/tokens'
import { ChainDefaultId, ChainId } from '@pancakeswap/sdk'
import chunk from 'lodash/chunk'
import sousChefV2 from '../../config/abi/sousChefV2.json'
import sousChefV3 from '../../config/abi/sousChefV3.json'

const livePoolsWithEnd = poolsConfig.filter((p) => !p.vaultKey && !p.isFinished)

export const fetchPoolsBlockLimits = async (chainId: ChainId) => {
  const pools = livePoolsWithEnd.filter(poolConfig => !!poolConfig.contractAddress[chainId])
  const startEndBlockCalls = pools.flatMap((poolConfig) => {
    return [
      {
        address: getAddress(poolConfig.contractAddress, chainId),
        name: 'startBlock',
      },
      {
        address: getAddress(poolConfig.contractAddress, chainId),
        name: 'bonusEndBlock',
      },
    ]
  })
  
  const startEndBlockRaw = await multicall(sousChefABI, startEndBlockCalls, chainId)
  const startEndBlockResult = startEndBlockRaw.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 2)

    if (!resultArray[chunkIndex]) {
      // eslint-disable-next-line no-param-reassign
      resultArray[chunkIndex] = [] // start a new chunk
    }

    resultArray[chunkIndex].push(item)

    return resultArray
  }, [])

  return pools.map((pool, index) => {
    const [[startBlock], [endBlock]] = startEndBlockResult[index]
    return {
      sousId: pool.sousId,
      startBlock: startBlock.toNumber(),
      endBlock: endBlock.toNumber(),
    }
  })
}

export const fetchPoolsTotalStaking = async (chainId: ChainId) => {
  const pools = poolsConfig.filter(poolConfig => !!poolConfig.contractAddress[chainId])
  const poolsBalanceOf = pools.map((poolConfig) => {
    return {
      address: findToken(poolConfig.stakingToken.symbol, chainId).address,
      name: 'balanceOf',
      params: [getAddress(poolConfig.contractAddress, chainId)],
    }
  })
  const poolsTotalStaked = await multicall(erc20ABI, poolsBalanceOf, chainId)
  return pools.map((p, index) => ({
    sousId: p.sousId,
    totalStaked: new BigNumber(poolsTotalStaked[index]).toJSON(),
    stakingToken: p.stakingToken
  }))
}

export const fetchPoolsTotalStakingDetailed = async (chainId: ChainId) => {
  const pools = poolsConfig.filter(poolConfig => !!poolConfig.contractAddress[chainId])
  const poolsBalanceOf = pools.map((poolConfig) => {
    return {
      address: findToken(poolConfig.stakingToken.symbol, chainId).address,
      name: 'balanceOf',
      params: [getAddress(poolConfig.contractAddress, chainId)],
    }
  })
  const poolsTotalStaked = await multicall(erc20ABI, poolsBalanceOf, chainId)
  return pools.map((p, index) => ({
    sousId: p.sousId,
    totalStaked: new BigNumber(poolsTotalStaked[index]).toJSON(),
    stakingToken: p.stakingToken
  }))
}


export const fetchPoolsStakingLimits = async (
  poolsWithStakingLimit: number[], chainId: ChainId
  ): Promise<{ [key: string]: { stakingLimit: BigNumber; numberBlocksForUserLimit: number } }> => {
  const validPools = poolsConfig
    .filter(poolConfig => !!poolConfig.contractAddress[chainId])
    .filter((p) => p.stakingToken.symbol !== 'ETH' && !p.isFinished && !p.vaultKey)
    .filter((p) => !poolsWithStakingLimit.includes(p.sousId))

    // Get the staking limit for each valid pool
  const poolStakingCalls = validPools
    .map((validPool) => {
      const contractAddress = getAddress(validPool.contractAddress, chainId)
      return ['hasUserLimit', 'poolLimitPerUser', 'numberBlocksForUserLimit'].map((method) => ({
        address: contractAddress,
        name: method,
      }))
    })
    .flat()

  const poolStakingResultRaw = await multicallv2({
    abi: sousChefV2,
    calls: poolStakingCalls,
    options: { requireSuccess: false },
    chainId
  })
  const chunkSize = poolStakingCalls.length / validPools.length
  const poolStakingChunkedResultRaw = chunk(poolStakingResultRaw.flat(), chunkSize)
  return fromPairs(
    poolStakingChunkedResultRaw.map((stakingLimitRaw, index) => {
      const hasUserLimit = stakingLimitRaw[0]
      const stakingLimit = hasUserLimit && stakingLimitRaw[1] ? new BigNumber(stakingLimitRaw[1].toString()) : BIG_ZERO
      const numberBlocksForUserLimit = stakingLimitRaw[2] ? (stakingLimitRaw[2] as EthersBigNumber).toNumber() : 0
      return [validPools[index].sousId, { stakingLimit, numberBlocksForUserLimit }]
    }),
  )
}

const livePoolsWithV3 = poolsConfig.filter((pool) => pool?.version === 3 && !pool?.isFinished)

export const fetchPoolsProfileRequirement = async (): Promise<{
  [key: string]: {
    required: boolean
    thresholdPoints: string
  }
}> => {
  const poolProfileRequireCalls = livePoolsWithV3
    .map((validPool) => {
      const contractAddress = getAddress(validPool.contractAddress, ChainDefaultId)
      return ['pancakeProfileIsRequested', 'pancakeProfileThresholdPoints'].map((method) => ({
        address: contractAddress,
        name: method,
      }))
    })
    .flat()

  const poolProfileRequireResultRaw = await multicallv2({
    abi: sousChefV3,
    calls: poolProfileRequireCalls,
    options: { requireSuccess: false },
  })
  const chunkSize = poolProfileRequireCalls.length / livePoolsWithV3.length
  const poolStakingChunkedResultRaw = chunk(poolProfileRequireResultRaw.flat(), chunkSize)
  return fromPairs(
    poolStakingChunkedResultRaw.map((poolProfileRequireRaw, index) => {
      const hasProfileRequired = poolProfileRequireRaw[0]
      const profileThresholdPoints = poolProfileRequireRaw[1]
        ? new BigNumber(poolProfileRequireRaw[1].toString())
        : BIG_ZERO
      return [
        livePoolsWithV3[index].sousId,
        {
          required: !!hasProfileRequired,
          thresholdPoints: profileThresholdPoints.toJSON(),
        },
      ]
    }),
  )
}
